
import { Publication, PublicationStatus, PublicationTarget } from '@bruce.work/bruce-doc-class-typescript/doc/Class/Publication';
import { Recorded } from '@bruce.work/bruce-doc-event-typescript/doc/Class';
import { Piece } from '@bruce.work/bruce-doc-event-typescript/doc/Event/Query/build';
import { BACKGROUND_COLOR } from 'interfaces';
import styled from 'styled-components';
import Button_v2 from '@/components/molecules/Button/index';
import PublicationCard from '@/components/molecules/PublicationCard';
import Title_v2 from '@/components/molecules/Title/index';
import { ButtonProps, TitleProps } from '@/queries/components/molecules';
import devices from '@/utilities/devices';
import { renderUnderline } from '@/utilities/functions';

// #region STYLE
const SectionWithMissionCardContainer = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
	gap: 24px;
	margin-bottom: 3rem;
	max-width: 1200px;
	margin-left: auto;
	margin-right: auto;
	padding: 0 1rem;

	@media screen and (${devices.mobile}) {
		grid-template-columns: 1fr;
		gap: 20px;
		margin-top: 2rem;
		padding: 0;
	}

	@media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
		grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
		gap: 20px;
		margin-top: 2rem;
	}

	@media screen and (min-width: 1024px) {
		grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
		gap: 24px;
	}
`;
// #endregion

// #region QUERY
export const queryForPublications = (params: { field?: any; limit?: number; order?: any; start?: number; } = {}): Piece<Publication> => {
	return {
		data: {
			class: {
				fields: [
					'address',
					'body',
					'code',
					'createdAt',
					'mission.category',
					'mission.feature',
					'mission.job.image',
					'objectId',
					'organization.name',
					'publishedSince',
					'target',
					'targetData',
					'title',
				],
				name: 'Publication',
			},
			where: {
				field: {
					campaign: {
						'∉': [
							{ objectId: 'o4uv63VIk7', className: 'Campaign' },
							{ objectId: 'Ar1W0C3I8n', className: 'Campaign' },
							{ objectId: 'hDWGfWrVqj', className: 'Campaign' },
						],
					},
					tags: { '∌': 'manual' },
					target: { '=': PublicationTarget.INDEED },
					status: {
						'∈': [PublicationStatus.ACTIVE, PublicationStatus.PENDING, PublicationStatus.UNSYNCHRONIZED],
					},
				},
				limit: params.limit ?? 20,
				order: ['-', 'publishedSince'],
				start: 0,
			},
		},
	};
};
// #endregion

// #region INTERFACE
export interface SectionWithPublicationCardProps {
	readonly backgroundColor: {
		readonly color: BACKGROUND_COLOR;
	}
	readonly buttons: ButtonProps[];
	readonly cards: {
		readonly limit: number;
	},
	readonly title: TitleProps;
	readonly publications?: Recorded<Publication>[];
}
// #endregion

// #region COMPONENT
const SectionWithPublicationCard = ({ sectionWithMissionCardData, currentLang = 'fr' }:
{ sectionWithMissionCardData: SectionWithPublicationCardProps; currentLang?: string }) => {

	return (
		<>
			<Title_v2 underlineColor={sectionWithMissionCardData.title.underlineColor} padding='0rem 0 3rem 0'>
				{renderUnderline(sectionWithMissionCardData.title.titlePieces)}
			</Title_v2>
			<SectionWithMissionCardContainer>
				{sectionWithMissionCardData.publications?.slice(0, sectionWithMissionCardData.cards.limit).map(
					(publication) => (
						<PublicationCard key={publication.objectId} publication={publication} currentLang={currentLang} />
					),
				) ?? []}
			</SectionWithMissionCardContainer>
			{
				sectionWithMissionCardData.buttons && (
					sectionWithMissionCardData.buttons.map(
						(button, index) => (
							<Button_v2
								gtm_id={button.trackerId}
								href={button.url}
								key={index}
								openOnNewTab={button.newTab}
								text={button.text}
								version={button.variant}
							/>
						),
					)
				)
			}
		</>
	);
};
// #endregion

export default SectionWithPublicationCard;
